import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, AlertCircle, ExternalLink, Utensils } from 'lucide-react';

export interface ActiveOrder {
  id: string;
  restaurantName: string;
  itemName: string;
  orderedAt: Date;
  estimatedMinutes: number;
  notes?: string;
  url?: string;
  status: 'active' | 'arrived' | 'delayed';
}

interface SnackOrderTrackerProps {
  orders: ActiveOrder[];
  onMarkArrived: (orderId: string) => void;
  onMarkDelayed: (orderId: string) => void;
  onCheckStatus: (orderId: string, url: string) => void;
  onOpenSnackPanel?: () => void;
}

export const SnackOrderTracker: React.FC<SnackOrderTrackerProps> = ({
  orders,
  onMarkArrived,
  onMarkDelayed,
  onCheckStatus,
  onOpenSnackPanel,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second for live countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getTimeRemaining = (order: ActiveOrder) => {
    const estimatedArrival = new Date(order.orderedAt.getTime() + order.estimatedMinutes * 60000);
    const remaining = estimatedArrival.getTime() - currentTime.getTime();
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    
    if (remaining < 0) {
      return { overdue: true, display: `${Math.abs(minutes)} mins overdue`, progress: 100 };
    }
    
    const progress = ((order.estimatedMinutes * 60000 - remaining) / (order.estimatedMinutes * 60000)) * 100;
    return { overdue: false, display: `${minutes}:${seconds.toString().padStart(2, '0')}`, progress };
  };

  const formatOrderTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const activeOrders = orders.filter(o => o.status === 'active');

  if (activeOrders.length === 0) {
    return (
      <div className="rounded-2xl border border-purple-400/30 bg-purple-900/20 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-purple-200 font-semibold">SNACK TRACKER</p>
          {onOpenSnackPanel && (
            <button
              onClick={onOpenSnackPanel}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pink-500/20 text-pink-200 hover:bg-pink-500/30 font-semibold transition-all text-xs"
            >
              <Utensils className="w-3.5 h-3.5" />
              Snacks
            </button>
          )}
        </div>
        <div className="text-center py-8 text-gray-400 text-sm">
          <p>No active orders</p>
          <p className="text-xs mt-1">Order snacks from the panel to track them here!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-purple-400/30 bg-purple-900/20 p-4 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-purple-200 font-semibold">
          SNACK TRACKER ({activeOrders.length})
        </p>
        {onOpenSnackPanel && (
          <button
            onClick={onOpenSnackPanel}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pink-500/20 text-pink-200 hover:bg-pink-500/30 font-semibold transition-all text-xs"
          >
            <Utensils className="w-3.5 h-3.5" />
            Snacks
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {activeOrders.map((order) => {
          const timeInfo = getTimeRemaining(order);
          const isAlmostHere = timeInfo.progress >= 90 && !timeInfo.overdue;
          const isHalfway = timeInfo.progress >= 50 && timeInfo.progress < 90;

          return (
            <div
              key={order.id}
              className={`rounded-lg border p-3 space-y-2 ${
                timeInfo.overdue
                  ? 'border-red-500/40 bg-red-900/20'
                  : isAlmostHere
                  ? 'border-emerald-500/40 bg-emerald-900/20'
                  : 'border-purple-500/30 bg-black/30'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-white">{order.restaurantName}</h4>
                  <p className="text-xs text-gray-300">{order.itemName}</p>
                </div>
                {timeInfo.overdue && (
                  <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                )}
              </div>

              {/* Time Info */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">
                  Ordered: {formatOrderTime(order.orderedAt)}
                </span>
                <span className={`font-semibold ${
                  timeInfo.overdue ? 'text-red-400' : 
                  isAlmostHere ? 'text-emerald-400' : 
                  'text-purple-300'
                }`}>
                  {timeInfo.overdue ? timeInfo.display : `ETA: ${timeInfo.display}`}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="space-y-1">
                <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-1000 ${
                      timeInfo.overdue
                        ? 'bg-gradient-to-r from-red-500 to-red-400'
                        : isAlmostHere
                        ? 'bg-gradient-to-r from-emerald-500 to-green-400 animate-pulse'
                        : isHalfway
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-400'
                        : 'bg-gradient-to-r from-purple-500 to-indigo-400'
                    }`}
                    style={{ width: `${Math.min(timeInfo.progress, 100)}%` }}
                  />
                </div>
                <p className="text-[9px] text-gray-500 italic">
                  {timeInfo.overdue
                    ? '⚠️ Order is running late'
                    : isAlmostHere
                    ? '🔔 Almost here! Get ready!'
                    : isHalfway
                    ? '⏰ Halfway there!'
                    : '📦 Order in progress'}
                </p>
              </div>

              {/* Notes */}
              {order.notes && (
                <p className="text-[10px] text-gray-400 italic">Note: {order.notes}</p>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => onMarkArrived(order.id)}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-emerald-600/20 border border-emerald-500/30 rounded text-[10px] text-emerald-300 hover:bg-emerald-600/30 transition-colors font-semibold"
                >
                  <CheckCircle className="h-3 w-3" />
                  Arrived
                </button>
                {timeInfo.overdue && (
                  <button
                    onClick={() => onMarkDelayed(order.id)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-amber-600/20 border border-amber-500/30 rounded text-[10px] text-amber-300 hover:bg-amber-600/30 transition-colors font-semibold"
                  >
                    <Clock className="h-3 w-3" />
                    Still Waiting
                  </button>
                )}
                {order.url && (
                  <button
                    onClick={() => onCheckStatus(order.id, order.url)}
                    className="flex items-center justify-center gap-1 px-2 py-1.5 bg-purple-600/20 border border-purple-500/30 rounded text-[10px] text-purple-300 hover:bg-purple-600/30 transition-colors font-semibold"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Check
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
